/**
 * Live CALIBRATION — bow and masthead wind-sensor offsets for ONE boat.
 *
 * Brush a range on the coverage timeline, and every violin and every number on
 * the page is computed from that boat's live rows inside it.
 *
 * WHY A PARQUET READ IS OK HERE. The live SCATTER/PROBABILITY pages were removed
 * in August because they read the whole day of live parquet for all 13 boats
 * with every channel, on every load and every poll — tens of seconds, growing
 * through the session. This page is one boat, a bounded window (capped, default
 * the last hour), twelve projected channels via `?channels=`, and no polling at
 * all: it re-queries only when the brush, the boat, the sector or Refresh
 * changes. A window inside the stream server's 15-minute ring never touches
 * DuckDB; a longer one opens only the flush files whose filename span
 * intersects the window.
 *
 * The source selection here is PAGE-LOCAL on purpose. It is deliberately not
 * filterStore.selectedSources: calibrating one boat must not change which boats
 * the live map, fleet summary or timeseries pages are showing.
 */

import { createSignal, createMemo, createEffect, createSelector, onMount, onCleanup, For, Show } from "solid-js";
import { FiRefreshCw } from "solid-icons/fi";

import { persistantStore } from "../../../../store/persistantStore";
import { sourcesStore } from "../../../../store/sourcesStore";
import { setupMediaContainerScaling } from "../../../../utils/global";
import { logPageLoad } from "../../../../utils/logging";
import { debug as logDebug, error as logError } from "../../../../utils/console";
import { getLiveQueryDateYmd } from "../../../../utils/liveSessionDate";
import { ensureLiveDatasetsForProject } from "../../../../utils/liveDatasets";
import { ensureFleetSourcesContextReady } from "../../../../utils/fleetSourcesReady";
import { streamingDataService } from "../../../../services/streamingDataService";
import FleetSourcesGate from "../../../../components/utilities/FleetSourcesGate";
import DropDownButton from "../../../../components/buttons/DropDownButton";
import ScatterTimeseries from "../../../../components/charts/ScatterTimeseries";
import Violin from "../../../../components/charts/Violin";
import {
  fetchDailyTrainingCoverageTimeline,
  mapCoveragePointsToTimelinePoints,
  type DailyTrainingCoveragePoint,
} from "../../helpers/dailyTrainingSettingsApi";
import { SPEED_UNIT_KPH } from "../../../../utils/speedUnits";
import {
  FOILING_FILTER_OPTIONS,
  foilingFilterDescription,
  foilingFilterFromLabel,
  foilingFilterLabel,
  type FoilingFilter,
} from "../../../../utils/foilingStateFilter";
import type { CorrectedPair } from "../../../../utils/windTriangleMath";
import {
  LIVE_CALIBRATION_CHANNELS,
  PLAUSIBLE_OFFSET_DEG,
  SENSOR_CHANNELS,
  aggregateTacks,
  applyCorrections,
  buildViolinChart,
  buildViolinRows,
  defaultLiveWindow,
  offsetPlausibility,
  sampleAwa,
  sampleAws,
  solveSensor,
  toCalibrationSamples,
  windowMinutes,
  windowState,
  type CalibrationSample,
  type CalibrationSector,
  type CorrectedSample,
  type SensorName,
  type SensorSolution,
  type TimeWindow,
  type ViolinRow,
} from "../../../../utils/liveCalibrationData";

const { selectedClassName, selectedProjectId, selectedDate } = persistantStore;

const PAGE_TITLE = "CALIBRATION";
const PAGE_SUBTITLE =
  "Wind sensor calibration for one boat. Brush a time range on the timeline; the offsets and every violin below are computed from that boat's grade-2 live samples inside it. This page does not update live — press Refresh to re-query.";

/** Port red / starboard green, matching the day and dataset Calibration pages. */
const PORT_COLOR = "#c00";
const STBD_COLOR = "#2ca02c";

/** Boat to open on: the team's own, when the project has it. */
const PREFERRED_SOURCE_NAME = "GER";

/** One band per sensor, so BOW and MHU share a y-scale inside a single violin. */
const SENSOR_GROUPS = [
  { name: "BOW", color: "#1f77b4" },
  { name: "MHU", color: "#9467bd" },
];
const BSP_GROUPS = [{ name: "BSP", color: "#1f77b4" }];

function fmt(value: number | null | undefined, digits = 2): string {
  return value == null || !Number.isFinite(value) ? "—" : value.toFixed(digits);
}

/** Recommended values are signed, and which way to turn the sensor is the point, so colour by sign. */
function signedValueClass(value: number, suspect = false): string {
  if (suspect) {return "live-calibration-value live-calibration-value--suspect";}
  return value >= 0
    ? "live-calibration-value live-calibration-value--pos"
    : "live-calibration-value live-calibration-value--neg";
}

/** Signed display, so a positive recommendation reads as one. */
function fmtSigned(value: number, digits = 2): string {
  return value > 0 ? `+${fmt(value, digits)}` : fmt(value, digits);
}

export default function LiveCalibrationPage() {
  let mediaContainerRef: HTMLDivElement | undefined;

  // --- page-local state ---------------------------------------------------
  const [selectedSource, setSelectedSource] = createSignal<string | null>(null);
  const isSelectedSource = createSelector(selectedSource);
  const [sector, setSector] = createSignal<CalibrationSector>("upwind");
  const [foilingFilter, setFoilingFilter] = createSignal<FoilingFilter>("ALL");
  const [liveDate, setLiveDate] = createSignal(getLiveQueryDateYmd(selectedDate));
  const [refreshNonce, setRefreshNonce] = createSignal(0);
  const [refreshing, setRefreshing] = createSignal(false);

  const [coveragePoints, setCoveragePoints] = createSignal<DailyTrainingCoveragePoint[]>([]);
  const [timelineLoading, setTimelineLoading] = createSignal(false);
  const [timelineError, setTimelineError] = createSignal<string | null>(null);

  const [brushedRange, setBrushedRange] = createSignal<TimeWindow | null>(null);
  const [windowNotice, setWindowNotice] = createSignal<string | null>(null);

  const [rows, setRows] = createSignal<Record<string, unknown>[]>([]);
  const [rowsLoading, setRowsLoading] = createSignal(false);
  const [rowsError, setRowsError] = createSignal<string | null>(null);


  /**
   * Reading `Date.now()` inside the window memo would make it non-reactive and
   * stale. This is the "now" the default window is anchored to, and Refresh
   * moves it.
   */
  const [nowAnchor, setNowAnchor] = createSignal(Date.now());

  // --- source strip -------------------------------------------------------
  const sourceOptions = createMemo(() =>
    sourcesStore
      .sources()
      .map((s) => String(s.source_name ?? "").trim())
      .filter((n) => n !== "")
  );

  /** Open on GER when the project has it, and never leave a stale pick. */
  createEffect(() => {
    const names = sourceOptions();
    if (names.length === 0) {return;}
    const current = selectedSource();
    if (current && names.includes(current)) {return;}
    const preferred = names.find(
      (n) => n.toLowerCase() === PREFERRED_SOURCE_NAME.toLowerCase()
    );
    setSelectedSource(preferred ?? names[0]);
  });

  const selectSource = (name: string) => {
    if (name === selectedSource()) {return;}
    setSelectedSource(name);
    // A brushed window belongs to the boat it was made on.
    setBrushedRange(null);
    setWindowNotice(null);
    setRows([]);
  };

  // --- the time window ----------------------------------------------------
  const activeWindow = createMemo<TimeWindow>(() => brushedRange() ?? defaultLiveWindow(nowAnchor()));
  const activeWindowState = createMemo(() => windowState(activeWindow()));

  const windowLabel = createMemo(() => {
    const w = activeWindow();
    const time = (d: Date) =>
      d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    const scope = brushedRange() ? "brushed" : "last hour";
    return `${time(w.start)} – ${time(w.end)} · ${windowMinutes(w)} min (${scope})`;
  });

  const handleBrush = (detail: { eventIds: number[]; startTime: Date | null; endTime: Date | null }) => {
    if (detail.startTime == null || detail.endTime == null) {
      setBrushedRange(null);
      setWindowNotice(null);
      return;
    }
    const range = { start: detail.startTime, end: detail.endTime };
    if (windowState(range) === "tooLong") {
      // Refuse rather than fetch: keep whatever is on screen and say why.
      setWindowNotice(
        `That range is ${windowMinutes(range)} minutes. Brush 180 minutes or less — a longer window sweeps most of the session's parquet files.`
      );
      return;
    }
    setWindowNotice(
      windowState(range) === "warn"
        ? `${windowMinutes(range)} minutes: this reads beyond the in-memory window and may take a few seconds.`
        : null
    );
    setBrushedRange(range);
  };

  // --- timeline fetch -----------------------------------------------------
  let timelineSeq = 0;
  let timelineController: AbortController | null = null;

  createEffect(() => {
    const source = selectedSource();
    const legType = sector() === "downwind" ? ("downwind" as const) : ("upwind" as const);
    const date = liveDate();
    refreshNonce();

    const className = selectedClassName();
    const projectId = selectedProjectId();
    if (!source || !className || !projectId || !date) {
      setCoveragePoints([]);
      return;
    }

    timelineController?.abort();
    const controller = new AbortController();
    timelineController = controller;
    const seq = ++timelineSeq;

    setTimelineLoading(true);
    void (async () => {
      try {
        const result = await fetchDailyTrainingCoverageTimeline({
          className,
          projectId,
          legType,
          speedUnit: SPEED_UNIT_KPH,
          twsBin: null,
          sourceNames: [source],
          startDate: date,
          endDate: date,
          filters: {},
          liveOnly: true,
          signal: controller.signal,
        });
        if (seq !== timelineSeq || controller.signal.aborted) {return;}
        if (!result.ok) {
          if (result.message !== "Request cancelled") {
            setTimelineError(result.message);
            setCoveragePoints([]);
          }
          return;
        }
        setTimelineError(null);
        setCoveragePoints(result.data.coverage_points);
      } catch (err) {
        if (seq !== timelineSeq) {return;}
        logError("LiveCalibration: coverage timeline failed", err);
        setTimelineError(err instanceof Error ? err.message : "Failed to load the coverage timeline.");
        setCoveragePoints([]);
      } finally {
        if (seq === timelineSeq) {setTimelineLoading(false);}
      }
    })();
  });

  onCleanup(() => timelineController?.abort());

  const timelinePoints = createMemo(() =>
    mapCoveragePointsToTimelinePoints(coveragePoints(), SPEED_UNIT_KPH)
  );

  /**
   * The strip is one boat, so colouring it by source would be a single flat colour.
   * Tack is the useful dimension here and it matches the violins: port red,
   * starboard green, from the sign of the segment's TWA.
   */
  const TIMELINE_TACK_GROUPS = [
    { name: "PORT", color: PORT_COLOR },
    { name: "STBD", color: STBD_COLOR },
  ];

  const timelineGroups = createMemo((): Array<{ name: string; color: string }> => {
    const pts = timelinePoints() as Record<string, unknown>[];
    if (pts.length === 0) {return [];}
    const present = new Set(pts.map((p) => String(p.TACK ?? "")));
    const groups = TIMELINE_TACK_GROUPS.filter((g) => present.has(g.name));
    return groups.length > 0 ? groups : TIMELINE_TACK_GROUPS;
  });

  // --- live row fetch -----------------------------------------------------
  let rowsSeq = 0;
  let rowsController: AbortController | null = null;

  createEffect(() => {
    const source = selectedSource();
    const w = activeWindow();
    refreshNonce();

    if (!source || activeWindowState() === "tooLong") {
      return;
    }

    rowsController?.abort();
    const controller = new AbortController();
    rowsController = controller;
    const seq = ++rowsSeq;

    setRowsLoading(true);
    void (async () => {
      try {
        const fetched = await streamingDataService.fetchMergedDataProjected(
          source,
          [...LIVE_CALIBRATION_CHANNELS],
          w.start.getTime(),
          w.end.getTime(),
          controller.signal
        );
        if (seq !== rowsSeq || controller.signal.aborted) {return;}
        setRowsError(null);
        setRows(fetched as Record<string, unknown>[]);
        logDebug("LiveCalibration: rows loaded", { source, rows: fetched.length });
      } catch (err) {
        if (seq !== rowsSeq) {return;}
        logError("LiveCalibration: live row fetch failed", err);
        setRowsError(err instanceof Error ? err.message : "Failed to load live data.");
        setRows([]);
      } finally {
        if (seq === rowsSeq) {setRowsLoading(false);}
      }
    })();
  });

  onCleanup(() => rowsController?.abort());

  // --- samples, fits, violins ---------------------------------------------
  const sampleResult = createMemo(() => {
    const w = activeWindow();
    return toCalibrationSamples(rows(), {
      startMs: w.start.getTime(),
      endMs: w.end.getTime(),
      sector: sector(),
      foiling: foilingFilter(),
    });
  });

  const samples = createMemo<CalibrationSample[]>(() => sampleResult().samples);

  const bowSolution = createMemo<SensorSolution>(() =>
    solveSensor(aggregateTacks(samples(), "bow"), "bow")
  );
  const mhuSolution = createMemo<SensorSolution>(() =>
    solveSensor(aggregateTacks(samples(), "mhu"), "mhu")
  );

  const solutions = createMemo(() => [bowSolution(), mhuSolution()]);

  const corrected = createMemo<Record<SensorName, CorrectedSample[]>>(() => {
    const rowsFor = (s: SensorSolution): CorrectedSample[] =>
      s.fit === null ? [] : applyCorrections(samples(), s.sensor, s.fit.off, s.fit.mast);
    return { bow: rowsFor(bowSolution()), mhu: rowsFor(mhuSolution()) };
  });

  /** One violin dataset built from both sensors, so the bands share a y-scale. */
  const sensorViolin = (pick: (s: CalibrationSample, sensor: SensorName) => number | null): ViolinRow[] => {
    const rowsOut: ViolinRow[] = [];
    for (const sensor of ["bow", "mhu"] as SensorName[]) {
      rowsOut.push(
        ...buildViolinRows(
          samples(),
          SENSOR_CHANNELS[sensor].label,
          (s) => pick(s, sensor),
          (s) => s.twa
        )
      );
    }
    return rowsOut;
  };

  const correctedViolin = (pick: (c: CorrectedSample) => number | null): ViolinRow[] => {
    const rowsOut: ViolinRow[] = [];
    const byName = corrected();
    for (const sensor of ["bow", "mhu"] as SensorName[]) {
      rowsOut.push(
        ...buildViolinRows(byName[sensor], SENSOR_CHANNELS[sensor].label, pick, (c) => c.twaSigned)
      );
    }
    return rowsOut;
  };

  const bspRows = createMemo(() =>
    buildViolinRows(samples(), "BSP", (s) => s.bsp, (s) => s.twa)
  );
  const awaRows = createMemo(() => sensorViolin((s, sensor) => {
    const v = sampleAwa(s, sensor);
    return v === null ? null : Math.abs(v);
  }));
  const awsRows = createMemo(() => sensorViolin((s, sensor) => sampleAws(s, sensor)));
  const twsCorrRows = createMemo(() => correctedViolin((c) => c.twsCorr));
  const awaCorrRows = createMemo(() => correctedViolin((c) => Math.abs(c.awaCorr)));
  const twaCorrRows = createMemo(() => correctedViolin((c) => Math.abs(c.twaCorr)));

  const targetLabel = createMemo(() => {
    const target = bowSolution().targetAbsTwa ?? mhuSolution().targetAbsTwa;
    return target == null ? "TWA corrected (°)" : `TWA corrected (°) · target ${fmt(target, 1)}°`;
  });

  const violinCells = createMemo(() => [
    { title: "BSP", yLabel: "BSP (kph)", rows: bspRows(), groups: BSP_GROUPS },
    { title: "AWA — measured", yLabel: "|AWA| (°)", rows: awaRows(), groups: SENSOR_GROUPS },
    { title: "AWS — measured", yLabel: "AWS (kph)", rows: awsRows(), groups: SENSOR_GROUPS },
    { title: "TWS — corrected", yLabel: "TWS (kph)", rows: twsCorrRows(), groups: SENSOR_GROUPS },
    { title: "AWA — corrected", yLabel: "|AWA| (°)", rows: awaCorrRows(), groups: SENSOR_GROUPS },
    { title: "TWA — corrected", yLabel: targetLabel(), rows: twaCorrRows(), groups: SENSOR_GROUPS },
  ]);

  // --- refresh ------------------------------------------------------------
  const handleRefresh = async (): Promise<void> => {
    if (refreshing()) {return;}
    setRefreshing(true);
    try {
      setLiveDate(getLiveQueryDateYmd(selectedDate));
      setNowAnchor(Date.now());
      const className = selectedClassName();
      const projectId = selectedProjectId();
      if (className && projectId) {
        await ensureLiveDatasetsForProject(Number(projectId), className);
      }
      setRefreshNonce((n) => n + 1);
    } finally {
      setRefreshing(false);
    }
  };

  // --- mount --------------------------------------------------------------
  onMount(() => {
    // Registered before any await: an onCleanup after one would never run.
    const cleanupScaling = setupMediaContainerScaling({
      logPrefix: "LiveCalibration",
      scaleToWidth: true,
      getMediaContainer: () => mediaContainerRef ?? null,
    });
    onCleanup(() => cleanupScaling());

    void (async () => {
      logPageLoad("live/reports/LiveCalibration.tsx", PAGE_TITLE, "Loaded");
      const className = selectedClassName();
      const projectId = selectedProjectId();
      await ensureFleetSourcesContextReady({ className, projectId });
      if (className && projectId) {
        await ensureLiveDatasetsForProject(Number(projectId), className);
      }
      setLiveDate(getLiveQueryDateYmd(selectedDate));
    })();
  });

  // --- empty-state copy ---------------------------------------------------
  const emptyReason = createMemo((): string | null => {
    if (!selectedSource()) {return "Select a boat.";}
    if (activeWindowState() === "tooLong") {return "Brush a shorter range to load data.";}
    if (rowsLoading()) {return null;}
    if (rows().length === 0) {
      return "No live rows in this window. The boat may not have been streaming, or the window predates the session.";
    }
    if (samples().length === 0) {
      const r = sampleResult().rejections;
      const parts: string[] = [];
      if (r.lowGrade > 0) {parts.push(`${r.lowGrade} below grade 2`);}
      if (r.outsideSector > 0) {parts.push(`${r.outsideSector} outside the ${sector()} sector`);}
      if (r.lowBsp > 0) {parts.push(`${r.lowBsp} not sailing`);}
      if (r.wrongFoilingState > 0) {
        parts.push(`${r.wrongFoilingState} outside ${foilingFilterDescription(foilingFilter()).toLowerCase()}`);
      }
      if (r.noTack > 0) {parts.push(`${r.noTack} with no tack`);}
      if (r.noGrade > 0) {parts.push(`${r.noGrade} with no grade`);}
      if (r.outsideWindow > 0) {parts.push(`${r.outsideWindow} outside the window`);}
      return `None of the ${r.total} rows passed the filters (${parts.join(", ")}).`;
    }
    return null;
  });

  return (
    <div
      ref={mediaContainerRef}
      id="media-container"
      class="cheat-sheet-page training-summary-page live-calibration-page"
    >
      <FleetSourcesGate message="Loading fleet sources...">
        <div class="container relative">
          <div class="performance-charts-scroll-container">
            <div class="performance-charts-scroll-inner cheat-sheet-scroll-inner">
              <header class="training-summary-header">
                <h1 class="report-page-title cheat-sheet-title">{PAGE_TITLE}</h1>
                <p class="training-summary-subtitle">{PAGE_SUBTITLE}</p>
              </header>

              <div class="live-calibration-controls">
                <div class="live-calibration-control-buttons">
                  <DropDownButton
                    options={["UPWIND", "DOWNWIND"]}
                    defaultText={sector() === "downwind" ? "DOWNWIND" : "UPWIND"}
                    handleSelection={(value: string) =>
                      setSector(value === "DOWNWIND" ? "downwind" : "upwind")
                    }
                    smallLabel="Uw/Dw"
                    size="medium"
                  />
                  <DropDownButton
                    options={[...FOILING_FILTER_OPTIONS]}
                    defaultText={foilingFilterLabel(foilingFilter())}
                    handleSelection={(value: string) => setFoilingFilter(foilingFilterFromLabel(value))}
                    smallLabel="State"
                    size="big"
                  />
                  <button
                    type="button"
                    class="live-fleet-summary-refresh-button"
                    onClick={() => void handleRefresh()}
                    disabled={refreshing()}
                    title="Re-query today's live data"
                    aria-label="Refresh live data"
                  >
                    <FiRefreshCw
                      class={
                        refreshing()
                          ? "live-fleet-summary-refresh-icon spinning"
                          : "live-fleet-summary-refresh-icon"
                      }
                      size={16}
                      aria-hidden="true"
                    />
                    <span>Refresh</span>
                  </button>
                  {/* The window every number on this page is computed over. It sits with the
                      controls because it is the scope, not a result. */}
                  <span class="live-calibration-window-chip" aria-live="polite">
                    {windowLabel()}
                    {rowsLoading() ? " · loading…" : ""}
                  </span>
                </div>

                {/* Same chip classes as the Live Table's per-page source filter, so the two
                    pages look alike — but single-select: this page computes one boat. */}
                <div class="live-calibration-source-filter-row">
                  <span class="realtime-table-source-filter-label">Boat</span>
                  <div
                    class="realtime-table-source-chips"
                    role="group"
                    aria-label="Boat to calibrate (one at a time)"
                  >
                    <For each={sourceOptions()}>
                      {(name) => {
                        const sourceColor = sourcesStore.getSourceColor(name);
                        return (
                          <button
                            type="button"
                            class="realtime-table-source-chip"
                            classList={{ "realtime-table-source-chip--active": isSelectedSource(name) }}
                            aria-pressed={isSelectedSource(name)}
                            style={
                              isSelectedSource(name) && sourceColor
                                ? `background-color: ${sourceColor}; border-color: ${sourceColor}; color: #fff;`
                                : ""
                            }
                            onClick={() => selectSource(name)}
                            title={`Calibrate ${name} (this page only — other live pages keep their own source selection)`}
                          >
                            {name}
                          </button>
                        );
                      }}
                    </For>
                  </div>
                </div>
              </div>

              <section class="training-settings-timeline-section">
                <p class="cheat-sheet-table-explanation">
                  Markers show today's live coverage for this boat by TWS bin, coloured by tack
                  (port red, starboard green). Brush a time range to set the window for everything
                  below. The timeline uses a wider point-of-sail filter than the violins, so it is a
                  coverage picker rather than the sample set itself.
                </p>
                <Show
                  when={timelinePoints().length > 0}
                  fallback={
                    <p class="live-calibration-note">
                      {timelineLoading()
                        ? "Loading coverage…"
                        : timelineError() ??
                          "No live coverage for this boat and sector today. The window below defaults to the last hour."}
                    </p>
                  }
                >
                  <div class="training-settings-bin-timeline maneuver-selection-timeline">
                    <ScatterTimeseries
                      aggregates={timelinePoints()}
                      color="TACK"
                      groups={timelineGroups()}
                      skipSelectedSourcesFilter={true}
                      colorByFieldOnly={true}
                      isHistoryPage={false}
                      onBrushSelect={handleBrush}
                    />
                  </div>
                </Show>
                <Show when={windowNotice()}>
                  <p class="live-calibration-notice">{windowNotice()}</p>
                </Show>
                <Show when={rowsError()}>
                  <p class="live-calibration-notice">{rowsError()}</p>
                </Show>
              </section>

              <section class="live-calibration-offsets">
                <h2 class="live-calibration-section-title">Recommendations &amp; Predicted Outcomes</h2>
                <p class="live-calibration-note">
                  The two suggested-offset columns are the numbers to apply: add the suggested AWA
                  offset to that sensor's measured AWA, and the mast rotation on top of it for the
                  masthead. Everything to the right is the predicted outcome — what the correction
                  does to the tack split and to the level. The offset is read from the |AWA| split
                  with the two tacks compared at matched boat speed, since |AWA| depends on how fast
                  the boat is going and an unmatched comparison reads a speed difference as a bias.
                </p>
                <table class="live-calibration-offsets-table">
                  <thead>
                    <tr>
                      <th>Sensor</th>
                      <th>n port / stbd</th>
                      <th class="live-calibration-suggested-col">Suggested AWA offset (°)</th>
                      <th class="live-calibration-suggested-col">Suggested Mast Rot Offset (°)</th>
                      <th>|AWA| P / S before</th>
                      <th>|AWA| P / S after</th>
                      <th>Δ|AWA| before → after</th>
                      <th>Δ|TWA| before → after</th>
                      <th>mean |TWA| after</th>
                      <th>Target</th>
                      <th>TWS P / S before</th>
                      <th>TWS P / S after</th>
                      <th>ΔTWD before → after</th>
                      <th>WING rot P / S</th>
                    </tr>
                  </thead>
                  <tbody>
                    <For each={solutions()}>
                      {(s) => {
                        const meanAfter = () =>
                          s.fit === null
                            ? null
                            : (Math.abs(s.fit.after.port.twa) + Math.abs(s.fit.after.stbd.twa)) / 2;
                        /**
                         * The measured side of the fit: the per-tack |AWA| the offset is derived
                         * from. Δ|AWA| is the raw tack split — 2x the offset — so watching it go to
                         * zero is the most direct check that the recommendation is doing what it
                         * claims, before any of the triangle maths is involved.
                         */
                        const absAwa = (pair: CorrectedPair) => ({
                          port: Math.abs(pair.port.awa),
                          stbd: Math.abs(pair.stbd.awa),
                        });
                        const dAwa = (pair: CorrectedPair) => {
                          const a = absAwa(pair);
                          return a.stbd - a.port;
                        };
                        return (
                          <tr>
                            <td>{SENSOR_CHANNELS[s.sensor].label}</td>
                            <td class="num">
                              {s.nPort} / {s.nStbd}
                            </td>
                            <td class="num live-calibration-suggested-col">
                              <Show when={s.fit?.offSolved} fallback={<span title={s.reason ?? "no solution"}>—</span>}>
                                <span
                                  class={signedValueClass(
                                    s.fit!.off,
                                    offsetPlausibility(s.fit!.off) === "suspect"
                                  )}
                                  title={
                                    offsetPlausibility(s.fit!.off) === "suspect"
                                      ? `Beyond ±${PLAUSIBLE_OFFSET_DEG}° — check the window rather than applying this`
                                      : `Add ${fmt(s.fit!.off)}° to ${SENSOR_CHANNELS[s.sensor].awa}`
                                  }
                                >
                                  {fmtSigned(s.fit!.off)}
                                </span>
                              </Show>
                            </td>
                            <td class="num live-calibration-suggested-col">
                              <Show when={s.sensor === "mhu"} fallback={<span>n/a</span>}>
                                <Show
                                  when={s.fit && s.fit.mastState === "fitted"}
                                  fallback={
                                    <span title={s.fit?.mastState === "unidentifiable"
                                      ? "No target TWA in this window, so the mast rotation cannot be pinned"
                                      : "The target TWA is not reachable within ±30° of rotation"}>—</span>
                                  }
                                >
                                  <span
                                    class={signedValueClass(s.fit!.mast)}
                                    title={`Apply ${fmt(s.fit!.mast)}° of mast rotation offset, on top of the AWA offset`}
                                  >
                                    {fmtSigned(s.fit!.mast)}
                                  </span>
                                </Show>
                              </Show>
                            </td>
                            <td class="num">
                              <Show when={s.fit} fallback="—">
                                {fmt(absAwa(s.fit!.before).port, 1)} / {fmt(absAwa(s.fit!.before).stbd, 1)}
                              </Show>
                            </td>
                            <td class="num">
                              <Show when={s.fit} fallback="—">
                                {fmt(absAwa(s.fit!.after).port, 1)} / {fmt(absAwa(s.fit!.after).stbd, 1)}
                              </Show>
                            </td>
                            <td class="num">
                              <Show when={s.fit} fallback="—">
                                {fmt(dAwa(s.fit!.before))} → {fmt(dAwa(s.fit!.after))}
                              </Show>
                            </td>
                            <td class="num">
                              <Show when={s.fit} fallback="—">
                                {fmt(s.fit!.before.dTwa)} → {fmt(s.fit!.after.dTwa)}
                              </Show>
                            </td>
                            <td class="num">{fmt(meanAfter(), 1)}</td>
                            <td class="num">{fmt(s.targetAbsTwa, 1)}</td>
                            <td class="num">
                              <Show when={s.windBefore} fallback="—">
                                {fmt(s.windBefore!.twsPort, 1)} / {fmt(s.windBefore!.twsStbd, 1)}
                              </Show>
                            </td>
                            <td class="num">
                              <Show when={s.windAfter} fallback="—">
                                {fmt(s.windAfter!.twsPort, 1)} / {fmt(s.windAfter!.twsStbd, 1)}
                              </Show>
                            </td>
                            <td class="num">
                              <Show
                                when={s.windBefore?.dTwd != null && s.windAfter?.dTwd != null}
                                fallback={<span title="No usable headings in this window">—</span>}
                              >
                                {fmt(s.windBefore!.dTwd as number, 1)} →{" "}
                                {fmt(s.windAfter!.dTwd as number, 1)}
                              </Show>
                            </td>
                            <td class="num">
                              {fmt(s.meanWingRotPort, 1)} / {fmt(s.meanWingRotStbd, 1)}
                            </td>
                          </tr>
                        );
                      }}
                    </For>
                  </tbody>
                </table>

                <For each={solutions()}>
                  {(s) => (
                    <Show when={!s.available || s.fit?.mastState === "unidentifiable" || s.fit?.mastState === "unreachable" || (s.fit?.offSolved && offsetPlausibility(s.fit.off) === "suspect")}>
                      <p class="live-calibration-note">
                        <strong>{SENSOR_CHANNELS[s.sensor].label}:</strong>{" "}
                        {!s.available
                          ? s.reason
                          : s.fit?.mastState === "unidentifiable"
                            ? "no target TWA in this window, so the mast rotation cannot be recommended — the tack split alone cannot see it."
                            : s.fit?.mastState === "unreachable"
                              ? "no mast rotation within ±30° puts the corrected |TWA| on the target."
                              : `offset is beyond ±${PLAUSIBLE_OFFSET_DEG}°, which is larger than a real sensor bias. Check the window for mis-graded rows or a maneuver before applying it.`}
                      </p>
                    </Show>
                  )}
                </For>

              </section>

              <Show
                when={emptyReason() === null}
                fallback={<p class="live-calibration-note">{emptyReason()}</p>}
              >
                <section class="live-calibration-grid">
                  <For each={violinCells()}>
                    {(cell) => (
                      <div class="live-calibration-cell">
                        <h3 class="fleet-calibration-metric-title">{cell.title}</h3>
                        <div class="live-calibration-violin-container">
                          <Show
                            when={cell.rows.length > 0}
                            fallback={<p class="live-calibration-note">No samples.</p>}
                          >
                            <Violin
                              chart={buildViolinChart(cell.yLabel, cell.groups)}
                              data={cell.rows}
                              twaField="Twa_signed"
                              portColor={PORT_COLOR}
                              stbdColor={STBD_COLOR}
                              showOverallMeanLine={true}
                            />
                          </Show>
                        </div>
                      </div>
                    )}
                  </For>
                </section>
              </Show>
            </div>
          </div>
        </div>
      </FleetSourcesGate>
    </div>
  );
}
